// src/components/admin/PaymentTracking.jsx
import React, { useState, useEffect } from 'react';
import { ArrowLeft, DollarSign, AlertCircle, CheckCircle, Calendar, Download } from 'lucide-react';
import ExcelJS from 'exceljs';
import { styles } from '../../styles/theme';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useStudentContext } from '../../contexts/StudentContext';

const PaymentTracking = ({ onBack }) => {
  const { allStudents } = useStudentContext();
  const [activeTab, setActiveTab] = useState('payment'); // 'payment' | 'camp-report'
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editingStudent, setEditingStudent] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [inactiveStudentModal, setInactiveStudentModal] = useState(null);
  const [inactiveFormData, setInactiveFormData] = useState({ reason: 'Moved away', dateStopped: new Date().toISOString().split('T')[0], notes: '' });
  const [savingInactive, setSavingInactive] = useState(false);
  const STOP_REASONS = ['Moved away', 'Cost', 'Schedule conflict', 'School change', 'Program not a fit', 'Graduated/Too old', 'Other'];

  // ── Monthly Camp Report State ──
  const now = new Date();
  const [reportMonth, setReportMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [expandedStudent, setExpandedStudent] = useState(null);


  useEffect(() => {
    loadPaymentData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStudents]);

  const loadPaymentData = () => {
    try {
      setLoading(true);
      const afterSchool = allStudents.filter(s =>
        !s.archived &&
        s.programStatus !== 'inactive' &&
        (s.programType === 'after-school-pickup' ||
          (s.programSelection?.toLowerCase().includes('after') &&
           s.programSelection?.toLowerCase().includes('school')))
      );

      const today = new Date(); today.setHours(0, 0, 0, 0);

      const studentsWithPayment = afterSchool.map(student => {
        const paidThrough = student.paidThroughDate ? parseDate(student.paidThroughDate) : null;
        const isOverdue = paidThrough ? paidThrough < today : true;
        const daysPastDue = paidThrough && isOverdue ?
          Math.ceil((today - paidThrough) / (1000 * 60 * 60 * 24)) : 0;
        return {
          ...student,
          paidThroughDate: (student.paidThroughDate && student.paidThroughDate !== '') ? student.paidThroughDate : 'Not set',
          lastPaymentDate: student.lastPaymentDate || 'Not set',
          isOverdue,
          daysPastDue,
          isPaused: student.onBreak === true,
        };
      });

      setStudents(studentsWithPayment);
    } catch (error) {
      console.error('Error loading payment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkInactiveFromPayment = async () => {
    if (!inactiveStudentModal) return;
    setSavingInactive(true);
    try {
      await updateDoc(doc(db, 'students', inactiveStudentModal.id), {
        programStatus: 'inactive',
        inactiveReason: inactiveFormData.reason,
        inactiveDate: inactiveFormData.dateStopped,
        inactiveNotes: inactiveFormData.notes,
      });
      setInactiveStudentModal(null);
      setInactiveFormData({ reason: 'Moved away', dateStopped: new Date().toISOString().split('T')[0], notes: '' });
    } catch (err) {
      alert('Failed to mark student as inactive.');
    } finally {
      setSavingInactive(false);
    }
  };

 // Parse date from MM-DD-YYYY format
const parseDate = (dateStr) => {
  if (!dateStr || dateStr === 'Not set') return null;
  
  // Handle MM-DD-YYYY format
  if (dateStr.includes('-')) {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const month = parts[0];
      const day = parts[1];
      const year = parts[2];
      
      // Create date in YYYY-MM-DD format for proper parsing
      const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      const date = new Date(formattedDate);
      
      // Check if valid date
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  }
  
  // Fallback: try direct parsing
  const date = new Date(dateStr);
  return !isNaN(date.getTime()) ? date : null;
};

  // Format date as MM-DD-YYYY
  const formatDateMMDDYYYY = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear());
    return `${month}-${day}-${year}`;
  };

  // Calculate paid through date (4 weeks = 20 business days from start date)
  const calculate4WeekDate = (startDate) => {
    const date = new Date(startDate);
    let businessDaysCount = 0;
    
    // Add 20 business days (4 weeks × 5 days)
    while (businessDaysCount < 20) {
      date.setDate(date.getDate() + 1);
      const dayOfWeek = date.getDay();
      
      // Count only weekdays (Mon-Fri)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        businessDaysCount++;
      }
    }
    
    return date;
  };

  // Get next Monday from a given date
  const getNextMonday = (fromDate) => {
    const date = new Date(fromDate);
    const dayOfWeek = date.getDay();
    
    // If today is Monday (1), use today. Otherwise find next Monday
    if (dayOfWeek === 1) {
      return date;
    }
    
    const daysUntilMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek);
    date.setDate(date.getDate() + daysUntilMonday);
    return date;
  };

  const handleUpdatePayment = (student) => {
    const today = new Date();
    const nextMonday = getNextMonday(today);
    const sessionsStartDateStr = nextMonday.toISOString().split('T')[0];
    
    // IMPORTANT: Calculate the paidThroughDate immediately when opening the modal
    const paidThroughDate = calculate4WeekDate(nextMonday);
    const paidThroughFormatted = formatDateMMDDYYYY(paidThroughDate);
    
    setEditingStudent({
      id: student.id,
      studentName: `${student.firstName || student.studentFirstName} ${student.lastName || student.studentLastName}`,
      paymentDate: today.toISOString().split('T')[0], // YYYY-MM-DD for input type="date"
      sessionsStartDate: sessionsStartDateStr, // Default to next Monday
      paidThroughDate: paidThroughFormatted, // Pre-calculated
      calculatedPaidThrough: paidThroughDate.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      })
    });
    setShowModal(true);
  };

  const handleSessionsStartChange = (dateStr) => {
    const startDate = new Date(dateStr);
    const paidThroughDate = calculate4WeekDate(startDate);
    
    setEditingStudent({
      ...editingStudent,
      sessionsStartDate: dateStr,
      paidThroughDate: formatDateMMDDYYYY(paidThroughDate),
      calculatedPaidThrough: paidThroughDate.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      })
    });
  };

  const handleSavePayment = async () => {
    try {
      if (!editingStudent.paymentDate || !editingStudent.sessionsStartDate) {
        alert('Please select both payment date and sessions start date');
        return;
      }

      if (!editingStudent.paidThroughDate) {
        alert('Error: Paid through date was not calculated');
        return;
      }

      const studentRef = doc(db, 'students', editingStudent.id);
      
      // Convert YYYY-MM-DD to MM-DD-YYYY for storage
      const paymentDateFormatted = formatDateMMDDYYYY(new Date(editingStudent.paymentDate));
      const sessionsStartFormatted = formatDateMMDDYYYY(new Date(editingStudent.sessionsStartDate));
      
      await updateDoc(studentRef, {
        lastPaymentDate: paymentDateFormatted,
        sessionsStartDate: sessionsStartFormatted,
        paidThroughDate: editingStudent.paidThroughDate, // Already in MM-DD-YYYY format
        lastPaymentUpdate: new Date().toISOString()
      });

      alert('Payment information updated!');
      setShowModal(false);
      loadPaymentData();
    } catch (error) {
      console.error('Error updating payment:', error);
      alert('Failed to update payment information');
    }
  };

  const pausedStudents = students.filter(s => s.isPaused);
  const billingStudents = students.filter(s => !s.isPaused);

  const filteredStudents = billingStudents.filter(s => {
    if (filter === 'overdue') return s.isOverdue;
    if (filter === 'paid') return !s.isOverdue;
    return true;
  });

  const inactiveCount = allStudents.filter(s =>
    !s.archived && s.programStatus === 'inactive' &&
    (s.programType === 'after-school-pickup' ||
      (s.programSelection?.toLowerCase().includes('after') &&
       s.programSelection?.toLowerCase().includes('school')))
  ).length;

  const stats = {
    total: billingStudents.length,
    overdue: billingStudents.filter(s => s.isOverdue).length,
    paid: billingStudents.filter(s => !s.isOverdue).length,
    paused: pausedStudents.length,
    inactive: inactiveCount,
  };

  // ── Monthly Camp Report Logic ──
  const parseCareDate = (str) => {
    if (!str) return null;
    const parts = str.split('-');
    if (parts.length !== 3) return null;
    const [m, d, y] = parts;
    const fullYear = parseInt(y) < 50 ? `20${y}` : `19${y}`;
    return new Date(`${fullYear}-${m}-${d}`);
  };

  const getStudentMonthData = (student, year, month) => {
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0, 23, 59, 59);
    const isBH = student.billingType === 'bright-horizons' || student.billingType === 'third-party';
    const isCamp = student.programSelection?.toLowerCase().includes('camp') ||
                   student.programSelection?.toLowerCase().includes('third-party');
    if (!isCamp || student.archived || student.programStatus === 'inactive') return null;
    let daysInMonth = [];
    if (student.careDates && Array.isArray(student.careDates)) {
      daysInMonth = student.careDates
        .map(c => parseCareDate(c.date))
        .filter(d => d && d >= monthStart && d <= monthEnd);
    }
    if (daysInMonth.length === 0 && student.campStartDate && student.campEndDate) {
      const start = new Date(student.campStartDate);
      const end = new Date(student.campEndDate);
      const from = start < monthStart ? monthStart : start;
      const to = end > monthEnd ? monthEnd : end;
      if (from <= to) {
        const cur = new Date(from);
        while (cur <= to) {
          const dow = cur.getDay();
          if (dow !== 0 && dow !== 6) daysInMonth.push(new Date(cur));
          cur.setDate(cur.getDate() + 1);
        }
      }
    }
    if (daysInMonth.length === 0) return null;
    return {
      student,
      isBH,
      daysCount: daysInMonth.length,
      days: daysInMonth.map(d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
    };
  };

  const getMonthlyReport = () => {
    const [year, month] = reportMonth.split('-').map(Number);
    const results = allStudents
      .map(s => getStudentMonthData(s, year, month))
      .filter(Boolean)
      .sort((a, b) => {
        if (a.isBH !== b.isBH) return a.isBH ? -1 : 1;
        return `${a.student.firstName}${a.student.lastName}`.localeCompare(`${b.student.firstName}${b.student.lastName}`);
      });
    return { bhStudents: results.filter(r => r.isBH), directStudents: results.filter(r => !r.isBH), total: results.length };
  };

  const exportMonthlyReport = async () => {
    const { bhStudents, directStudents } = getMonthlyReport();
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet('Monthly Camp Report');
    ws.columns = [
      { header: 'Type', key: 'type', width: 16 },
      { header: 'First Name', key: 'firstName', width: 15 },
      { header: 'Last Name', key: 'lastName', width: 15 },
      { header: 'Grade', key: 'grade', width: 8 },
      { header: 'Days in Month', key: 'days', width: 14 },
      { header: 'Parent', key: 'parent', width: 20 },
      { header: 'Email', key: 'email', width: 28 },
    ];
    [...bhStudents, ...directStudents].forEach(r => {
      ws.addRow({ type: r.isBH ? 'Bright Horizons' : 'Direct', firstName: r.student.firstName, lastName: r.student.lastName, grade: r.student.grade, days: r.daysCount, parent: r.student.parentName, email: r.student.parentEmail });
    });
    ws.getRow(1).font = { bold: true };
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `camp-report-${reportMonth}.xlsx`; a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: '2rem', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <button onClick={onBack} style={styles.secondaryButton}>
          <ArrowLeft size={16} />
          Back to Dashboard
        </button>
        
        <div style={{ marginTop: '1rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <DollarSign size={28} style={{ color: '#10b981' }} />
            Payment Tracking
          </h1>
          <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>
            4-week billing cycles for after-school programs
          </p>
        </div>
      </div>

      {/* Top Tab Bar */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid #e5e7eb', paddingBottom: '0' }}>
        {[['payment', '💰 Payment Tracking'], ['camp-report', '📊 Camp Monthly Report']].map(([tab, label]) => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: '0.6rem 1.25rem', border: 'none', borderBottom: activeTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
            backgroundColor: 'transparent', color: activeTab === tab ? '#3b82f6' : '#6b7280',
            fontWeight: activeTab === tab ? '700' : '400', fontSize: '0.9rem', cursor: 'pointer', marginBottom: '-2px',
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Camp Monthly Report Tab ── */}
      {activeTab === 'camp-report' && (() => {
        const { bhStudents, directStudents, total } = getMonthlyReport();
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>Month:</label>
                <input type="month" value={reportMonth} onChange={e => setReportMonth(e.target.value)}
                  style={{ padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' }} />
              </div>
              <button onClick={exportMonthlyReport} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600' }}>
                <Download size={16} /> Export Excel
              </button>
            </div>

            {/* Summary cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
              {[
                { label: 'Total Camp Students', value: total, color: '#3b82f6', bg: '#eff6ff' },
                { label: 'Bright Horizons', value: bhStudents.length, color: '#8b5cf6', bg: '#ede9fe' },
                { label: 'Direct Enrollment', value: directStudents.length, color: '#10b981', bg: '#f0fdf4' },
              ].map(s => (
                <div key={s.label} style={{ backgroundColor: s.bg, borderRadius: '10px', padding: '1.1rem 1.25rem', border: `1.5px solid ${s.color}30` }}>
                  <div style={{ fontSize: '2rem', fontWeight: '800', color: s.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.2rem', fontWeight: '600' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* BH Students */}
            {bhStudents.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '0.9rem', fontWeight: '700', color: '#6d28d9', marginBottom: '0.75rem' }}>
                  <span style={{ backgroundColor: '#ede9fe', padding: '0.2rem 0.6rem', borderRadius: '4px' }}>BH</span>
                  {' '}Bright Horizons ({bhStudents.length} students)
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {bhStudents.map(r => (
                    <div key={r.student.id} style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                      <div onClick={() => setExpandedStudent(expandedStudent === r.student.id ? null : r.student.id)}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.7rem 1rem', cursor: 'pointer' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>{r.student.firstName} {r.student.lastName}</span>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Grade {r.student.grade}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#6d28d9', backgroundColor: '#ede9fe', padding: '0.15rem 0.5rem', borderRadius: '4px' }}>{r.daysCount} days</span>
                          <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{expandedStudent === r.student.id ? '▲' : '▼'}</span>
                        </div>
                      </div>
                      {expandedStudent === r.student.id && (
                        <div style={{ padding: '0.6rem 1rem', backgroundColor: '#fafafa', borderTop: '1px solid #f3f4f6', fontSize: '0.78rem', color: '#6b7280' }}>
                          <div style={{ marginBottom: '0.25rem' }}><strong>Parent:</strong> {r.student.parentName} · {r.student.parentPhone} · {r.student.parentEmail}</div>
                          <div><strong>Days:</strong> {r.days.join(', ')}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Direct Students */}
            {directStudents.length > 0 && (
              <div>
                <h3 style={{ fontSize: '0.9rem', fontWeight: '700', color: '#065f46', marginBottom: '0.75rem' }}>
                  <span style={{ backgroundColor: '#d1fae5', padding: '0.2rem 0.6rem', borderRadius: '4px' }}>Direct</span>
                  {' '}Direct Enrollment ({directStudents.length} students)
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {directStudents.map(r => (
                    <div key={r.student.id} style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                      <div onClick={() => setExpandedStudent(expandedStudent === r.student.id ? null : r.student.id)}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.7rem 1rem', cursor: 'pointer' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>{r.student.firstName} {r.student.lastName}</span>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Grade {r.student.grade}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#065f46', backgroundColor: '#d1fae5', padding: '0.15rem 0.5rem', borderRadius: '4px' }}>{r.daysCount} days</span>
                          <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{expandedStudent === r.student.id ? '▲' : '▼'}</span>
                        </div>
                      </div>
                      {expandedStudent === r.student.id && (
                        <div style={{ padding: '0.6rem 1rem', backgroundColor: '#fafafa', borderTop: '1px solid #f3f4f6', fontSize: '0.78rem', color: '#6b7280' }}>
                          <div style={{ marginBottom: '0.25rem' }}><strong>Parent:</strong> {r.student.parentName} · {r.student.parentPhone} · {r.student.parentEmail}</div>
                          <div><strong>Days:</strong> {r.days.join(', ')}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {total === 0 && (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af', backgroundColor: '#f9fafb', borderRadius: '10px' }}>
                No camp students found for this month.
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Payment Tracking Tab ── */}
      {activeTab === 'payment' && (<>
      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <div style={{ ...styles.card, padding: '1.25rem', borderLeft: '4px solid #3b82f6' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e293b' }}>{stats.total}</div>
          <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '0.25rem' }}>Active Billing</div>
        </div>
        <div style={{ ...styles.card, padding: '1.25rem', borderLeft: '4px solid #dc2626' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e293b' }}>{stats.overdue}</div>
          <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '0.25rem' }}>Overdue</div>
        </div>
        <div style={{ ...styles.card, padding: '1.25rem', borderLeft: '4px solid #10b981' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e293b' }}>{stats.paid}</div>
          <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '0.25rem' }}>Paid Up</div>
        </div>
        <div style={{ ...styles.card, padding: '1.25rem', borderLeft: '4px solid #f59e0b' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e293b' }}>{stats.paused}</div>
          <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '0.25rem' }}>On Break</div>
        </div>
        <div style={{ ...styles.card, padding: '1.25rem', borderLeft: '4px solid #9ca3af' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#9ca3af' }}>{stats.inactive}</div>
          <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '0.25rem' }}>Inactive</div>
        </div>
      </div>

      {/* Paused students notice */}
      {pausedStudents.length > 0 && (
        <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px' }}>
          <div style={{ fontWeight: '700', color: '#92400e', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
            ⏸️ {pausedStudents.length} student(s) on break — not included in billing
          </div>
          {pausedStudents.map(s => (
            <div key={s.id} style={{ fontSize: '0.8rem', color: '#92400e' }}>
              · {s.firstName} {s.lastName}
              {s.breakEndDate && ` — returns ${s.breakEndDate}`}
              {s.breakReason && ` (${s.breakReason})`}
            </div>
          ))}
        </div>
      )}

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {[
          { val: 'all',     label: `All (${stats.total})`,         color: '#3b82f6' },
          { val: 'overdue', label: `Overdue (${stats.overdue})`,   color: '#dc2626' },
          { val: 'paid',    label: `Paid Up (${stats.paid})`,      color: '#10b981' },
        ].map(tab => (
          <button key={tab.val} onClick={() => setFilter(tab.val)} style={{ ...styles.button, backgroundColor: filter === tab.val ? tab.color : '#e2e8f0', color: filter === tab.val ? 'white' : '#64748b' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Students List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>Loading payment data...</div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {filteredStudents.map(student => (
            <div key={student.id} style={{ ...styles.card, padding: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', margin: 0 }}>
                      {student.firstName || student.studentFirstName} {student.lastName || student.studentLastName}
                    </h3>
                    {student.isOverdue ? (
                      <span style={{ ...styles.badge, backgroundColor: '#fee2e2', color: '#991b1b', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <AlertCircle size={14} /> Overdue
                      </span>
                    ) : (
                      <span style={{ ...styles.badge, backgroundColor: '#d1fae5', color: '#065f46', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <CheckCircle size={14} /> Paid
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>Last Payment</div>
                      <div style={{ fontSize: '0.875rem', color: '#1e293b', marginTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Calendar size={14} />{student.lastPaymentDate}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>Paid Through</div>
                      <div style={{ fontSize: '0.875rem', color: '#1e293b', marginTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Calendar size={14} />{student.paidThroughDate}
                        {student.isOverdue && student.paidThroughDate !== 'Not set' && (
                          <span style={{ color: '#dc2626', fontSize: '0.75rem', fontWeight: '600' }}>({student.daysPastDue} days overdue)</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>Billing Cycle</div>
                      <div style={{ fontSize: '0.875rem', color: '#1e293b', marginTop: '0.25rem' }}>4 weeks (28 days)</div>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginLeft: '1rem' }}>
                  <button onClick={() => handleUpdatePayment(student)} style={{ ...styles.button, backgroundColor: '#3b82f6', padding: '0.5rem 1rem' }}>
                    Record Payment
                  </button>
                  {student.isOverdue && (
                    <button
                      onClick={() => setInactiveStudentModal(student)}
                      style={{ padding: '0.4rem 0.75rem', backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}
                    >
                      Mark Inactive
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {filteredStudents.length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>No students found in this category</div>
          )}
        </div>
      )}

      {/* Mark Inactive Modal */}
      {inactiveStudentModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '420px' }}>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: '700' }}>Mark as Inactive</h3>
            <p style={{ margin: '0 0 1.25rem', fontSize: '0.85rem', color: '#6b7280' }}>
              {inactiveStudentModal.firstName} {inactiveStudentModal.lastName} will be removed from payment tracking but kept in All Students for records.
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', marginBottom: '0.3rem' }}>Reason *</label>
              <select value={inactiveFormData.reason} onChange={e => setInactiveFormData(f => ({ ...f, reason: e.target.value }))} style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }}>
                {STOP_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', marginBottom: '0.3rem' }}>Date Stopped *</label>
              <input type="date" value={inactiveFormData.dateStopped} onChange={e => setInactiveFormData(f => ({ ...f, dateStopped: e.target.value }))} style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', marginBottom: '0.3rem' }}>Notes (optional)</label>
              <textarea value={inactiveFormData.notes} onChange={e => setInactiveFormData(f => ({ ...f, notes: e.target.value }))} placeholder="Any additional context..." rows={2} style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setInactiveStudentModal(null)} style={{ flex: 1, padding: '0.7rem', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              <button onClick={handleMarkInactiveFromPayment} disabled={savingInactive} style={{ flex: 1, padding: '0.7rem', backgroundColor: savingInactive ? '#9ca3af' : '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                {savingInactive ? 'Saving...' : 'Mark Inactive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Payment Modal */}
      {showModal && editingStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px'
          }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>
              Record Payment - {editingStudent.studentName}
            </h2>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Payment Received Date *
              </label>
              <input
                type="date"
                value={editingStudent.paymentDate}
                onChange={(e) => setEditingStudent({ ...editingStudent, paymentDate: e.target.value })}
                style={styles.input}
              />
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                Date when payment was received (for Stripe integration)
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Sessions Start Date (Monday) *
              </label>
              <input
                type="date"
                value={editingStudent.sessionsStartDate}
                onChange={(e) => handleSessionsStartChange(e.target.value)}
                style={styles.input}
              />
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                Which Monday does the 4-week cycle begin?
              </div>
            </div>

            {editingStudent.calculatedPaidThrough && (
              <div style={{ 
                marginBottom: '1.5rem', 
                padding: '1rem', 
                backgroundColor: '#f0f9ff', 
                borderRadius: '6px',
                border: '1px solid #bfdbfe'
              }}>
                <div style={{ fontSize: '0.875rem', color: '#1e40af', fontWeight: '600', marginBottom: '0.25rem' }}>
                  Paid Through Date (auto-calculated)
                </div>
                <div style={{ fontSize: '1.125rem', color: '#1e293b', fontWeight: 'bold' }}>
                  {editingStudent.calculatedPaidThrough}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  20 business days (4 weeks Mon-Fri) from sessions start date
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowModal(false)}
                style={{ ...styles.button, backgroundColor: '#6b7280' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSavePayment}
                style={styles.button}
                disabled={!editingStudent.paymentDate || !editingStudent.sessionsStartDate}
              >
                Save Payment
              </button>
            </div>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
};

export default PaymentTracking;